import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ContactForm from './components/ContactForm';
import MobileMenu from '../../components/feature/MobileMenu';
import { useSEO } from '../../hooks/useSEO';
import JsonLd from '../../components/feature/JsonLd';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSEO({
    title: 'Contact Us | Get in Touch | Human-Centred Design Consultancy London',
    description: 'Ready to transform your challenge into impact? Contact our London-based human-centred design team. We work with healthcare, government, and social impact organizations worldwide.',
    keywords: 'contact us, get in touch, design consultancy London, project inquiry, collaboration, design services contact',
    canonical: '/contact',
    ogType: 'website',
    ogImage: 'https://public.readdy.ai/ai/img_res/3d7dd6f85591c7b15ce321ca4d30ae09.jpg'
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": "Contact Design For",
        "url": "https://example.com/contact",
        "description": "Get in touch with our London-based human-centred design team",
        "mainEntity": {
          "@type": "Organization",
          "name": "Design For",
          "email": "hello@designfor.uk",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "London",
            "addressCountry": "GB"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "hello@designfor.uk",
            "contactType": "Customer Service",
            "availableLanguage": "English"
          }
        }
      }} />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png" 
                alt="Design For" 
                className="h-16"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Home</Link>
              <Link to="/about" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">About</Link>
              <Link to="/services" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Services</Link>
              <Link to="/work" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Work</Link>
              <Link to="/resources" className="text-black hover:text-[#ff4858] transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Insights</Link>
              <Link to="/contact" className="bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-all duration-200 text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                Contact
              </Link>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-black"
            >
              <i className="ri-menu-line text-3xl"></i>
            </button>
          </div>
        </div>
      </nav>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/d2f9a300d0cb70229fc5583fc06613aa.jpeg"
            alt="Contact hero background"
            className="w-full h-full object-cover object-[center_20%]"
          />
          <div className="absolute inset-0 bg-black/35"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-sm uppercase tracking-widest text-white/80 font-bold mb-6">Get In Touch</p>
          <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight leading-[1.05] max-w-5xl">
            Let&apos;s design something <span className="text-white">meaningful</span> together.
          </h1>
          <p className="text-xl text-white/80 font-medium leading-relaxed max-w-3xl mt-8">
            Whether you&apos;re ready to start a project, have a question, or just want to explore what&apos;s possible — We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 xl:gap-16">
            {/* Left - Contact Info */}
            <div className="lg:col-span-4 space-y-12">
              <div className="space-y-8">
                <h2 className="text-3xl font-black text-black tracking-tight">Let&apos;s connect</h2>
                <p className="text-base text-gray-500 font-medium leading-relaxed">
                  We work with organisations across the globe. Based in London, available worldwide. Drop me a message and I&apos;ll get back to you within 24 hours.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">Email</p>
                    <a href="mailto:hello@designfor.uk" className="text-base font-bold text-black hover:text-primary transition-colors cursor-pointer">hello@designfor.uk</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">Location</p>
                    <p className="text-base font-bold text-black">London, United Kingdom</p>
                    <p className="text-sm text-gray-500 font-medium">Available globally</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary text-xl"></i>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">Response Time</p>
                    <p className="text-base font-bold text-black">Within 24 hours</p>
                    <p className="text-sm text-gray-500 font-medium">Mon – Fri, 9am – 6pm GMT</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">Connect on Social</p>
                <div className="flex gap-3">
                  <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-primary/10 hover:bg-primary rounded-xl flex items-center justify-center transition-all duration-300 group cursor-pointer">
                    <i className="ri-linkedin-fill text-primary group-hover:text-white text-xl transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Right - Contact Form */}
            <div className="lg:col-span-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-28 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/983b2cf2226e0ba05a8262de5bbfcb7c.jpeg"
            alt="FAQ background"
            className="w-full h-full object-cover object-[center_15%]"
          />
          <div className="absolute inset-0 bg-black/55"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-5xl lg:text-6xl font-black text-white tracking-tight mb-16">Frequently asked questions</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-lg font-black text-black mb-3">What types of organisations do you work with?</h3>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                We work across sectors — from healthcare and government to tech startups, retail and financial services. If you&apos;re solving something important for people, we&apos;d love to chat.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-lg font-black text-black mb-3">How do engagements typically start?</h3>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Usually with a conversation! We&apos;ll discuss your challenge, goals and context. From there we&apos;ll recommend the best approach — whether that&apos;s a workshop, sprint or longer engagement.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-lg font-black text-black mb-3">Do you work remotely or on-site?</h3>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Both! We&apos;re based in London but work with clients globally. Workshops and sprints can be delivered in-person or remotely depending on what works best for your team.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-lg font-black text-black mb-3">What&apos;s the typical timeline for a project?</h3>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                It depends on the scope. Workshops can be a few hours to a few days. Sprints run 1–8 weeks. Larger strategic engagements typically span 3–12 months.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png" 
                alt="Design For" 
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</Link>
              <Link to="/terms" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</Link>
              <Link to="/cookies" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
